'use es6';

import { bender } from 'hubspot';
import { Component } from 'react';
import { NavMarker } from 'react-rhumb';
import PropTypes from 'prop-types';
import I18n from 'I18n';
import FormattedMessage from 'I18n/components/FormattedMessage';
import UIErrorMessage from 'UIComponents/error/UIErrorMessage';
import UIFlex from 'UIComponents/layout/UIFlex';
import UIButton from 'UIComponents/button/UIButton';
import UILoadingSpinner from 'UIComponents/loading/UILoadingSpinner';
import { APP_ERRORS, CATEGORY_FROM_PROJECT, FATAL_ERROR_TYPES } from 'ContentEditorUI/lib/app/constants';
import Categories from 'ContentUtils/constants/Categories';
import ContentRoutes from 'ContentUtils/Routes';
import I18nReadyContainer from 'ContentEditorUI/containers/I18nReadyContainer';
import UsageTracker from 'ContentEditorUI/utils/UsageTracker';
import { fetch as fetchContents } from 'ContentEditorUI/api/ContentsApi';
import EditorConfigSingleton from 'ContentEditorUI/EditorConfigSingleton';
import withPathParams from 'ContentEditorUI/utils/withPathParams';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
class ErrorPage extends Component {
  constructor(props) {
    super(props);
    this.state = {
      category: null,
      isCategoryLoading: false
    };
  }
  componentDidMount() {
    const {
      appErrorCategory
    } = this.props;
    const category = this.getContentCategory();
    if (appErrorCategory === APP_ERRORS.NOT_FOUND && category === CATEGORY_FROM_PROJECT['page-editor-ui']) {
      this.getDeletedContentType();
    }
  }
  get isErrorPageExpected() {
    const {
      appErrorCategory
    } = this.props;
    const {
      NOT_FOUND,
      NOT_ALLOWED,
      SECURITY_VIOLATION
    } = APP_ERRORS;
    return [CATEGORY_FROM_PROJECT.EmailDragDropEditorUI, CATEGORY_FROM_PROJECT.EmailTemplateEditorUI].includes(this.getContentCategory()) && [NOT_FOUND, NOT_ALLOWED, SECURITY_VIOLATION].includes(appErrorCategory);
  }
  getContentCategory() {
    // Note: for pages, we attempt to fetch the subcategory from the cos content
    // api. If this request is successful the category will be set in state,
    // otherwise we default to landing pages and use hubspot.bender to determine
    // the current project.
    const category = this.state.category ? this.state.category : CATEGORY_FROM_PROJECT[bender.currentProject];
    return category;
  }
  getCategoryDashboardLink() {
    const category = this.getContentCategory();
    if (category === CATEGORY_FROM_PROJECT.EmailTemplateEditorUI) {
      return ContentRoutes.emailContentCreator();
    }
    return ContentRoutes.dashboard(category);
  }
  getDeletedContentType() {
    const {
      params
    } = this.props;
    const {
      contentId
    } = params;
    this.setState(() => {
      return {
        isCategoryLoading: true
      };
    });
    fetchContents(contentId).then(res => {
      this.setState(() => {
        return {
          category: Categories[res.contentTypeCategoryId].name
        };
      });
    }).catch(err => {
      console.log(err);
    }).finally(() => {
      this.setState(() => {
        return {
          isCategoryLoading: false
        };
      });
    });
  }
  langKey(suffix) {
    const {
      appErrorCategory
    } = this.props;
    return `app.errors.${appErrorCategory}.${suffix}.${this.getContentCategory()}`;
  }
  langKeyForCanContactSupport(suffix) {
    const {
      appErrorCategory,
      canContactSupport
    } = this.props;
    const supportSuffix = canContactSupport ? 'customer' : 'free-user';
    return `app.errors.${appErrorCategory}.${suffix}.${supportSuffix}`;
  }
  maybeRenderDashboardLink() {
    const {
      shouldNotRenderDashboardLink
    } = this.props;
    if (shouldNotRenderDashboardLink) {
      return null;
    }
    return /*#__PURE__*/_jsx(UIButton, {
      use: "primary",
      size: "default",
      href: this.getCategoryDashboardLink(),
      children: /*#__PURE__*/_jsx(FormattedMessage, {
        message: `app.errors.backToDashboardLink.${this.getContentCategory()}`
      })
    });
  }
  renderError() {
    const {
      appErrorCategory,
      securityState
    } = this.props;
    const {
      NOT_FOUND,
      NOT_ALLOWED,
      BAD_REQUEST,
      UNKNOWN,
      SECURITY_VIOLATION
    } = APP_ERRORS;
    const OverriddenError = EditorConfigSingleton.getIsOnScalableEditor() && EditorConfigSingleton.getComponents().ErrorPageOverride;
    if (OverriddenError) {
      return /*#__PURE__*/_jsx(OverriddenError, {
        appErrorCategory: appErrorCategory
      });
    }

    // Special error screen for content-permissioning-caused unauthorized error
    if (this.props.responseErrorType === FATAL_ERROR_TYPES.NOT_PERMISSIONED) {
      UsageTracker.trackEditorInteraction({
        action: 'Approvals Action',
        event: 'viewContentEditorAccessRequestTooltip'
      });
      return /*#__PURE__*/_jsxs(UIErrorMessage, {
        "data-test-id": "content-permissioning-error",
        illustration: "keys",
        title: I18n.text(this.langKey('notPermissioned.title')),
        children: [/*#__PURE__*/_jsx("p", {
          children: /*#__PURE__*/_jsx(FormattedMessage, {
            message: this.langKey('notPermissioned.nextStep')
          })
        }), this.maybeRenderDashboardLink()]
      });
    }
    switch (appErrorCategory) {
      case NOT_FOUND:
        {
          return /*#__PURE__*/_jsxs(UIErrorMessage, {
            type: appErrorCategory,
            children: [/*#__PURE__*/_jsx("p", {
              children: /*#__PURE__*/_jsx(FormattedMessage, {
                message: this.langKey('description')
              })
            }), this.maybeRenderDashboardLink()]
          });
        }
      case NOT_ALLOWED:
        {
          // illustration and title are overridden here because
          // there is no 'notAllowed' error message preset
          return /*#__PURE__*/_jsxs(UIErrorMessage, {
            illustration: "lock",
            title: I18n.text(this.langKey('title')),
            children: [/*#__PURE__*/_jsx("p", {
              children: /*#__PURE__*/_jsx(FormattedMessage, {
                message: this.langKey('description')
              })
            }), /*#__PURE__*/_jsx("p", {
              children: /*#__PURE__*/_jsx(FormattedMessage, {
                message: this.langKey('nextStep')
              })
            }), this.maybeRenderDashboardLink()]
          });
        }
      case BAD_REQUEST:
        {
          return /*#__PURE__*/_jsxs(UIErrorMessage, {
            type: appErrorCategory,
            children: [/*#__PURE__*/_jsx("p", {
              children: /*#__PURE__*/_jsx(FormattedMessage, {
                message: this.langKey('description')
              })
            }), /*#__PURE__*/_jsx("p", {
              children: /*#__PURE__*/_jsx(FormattedMessage, {
                message: this.langKeyForCanContactSupport('nextStep')
              })
            })]
          });
        }
      case SECURITY_VIOLATION:
        {
          return /*#__PURE__*/_jsxs(UIErrorMessage, {
            type: "expired",
            title: I18n.text(this.langKey(`${securityState}.title`)),
            children: [/*#__PURE__*/_jsx("p", {
              children: /*#__PURE__*/_jsx(FormattedMessage, {
                message: this.langKey(`${securityState}.description`)
              })
            }), this.maybeRenderDashboardLink()]
          });
        }
      case UNKNOWN:
      default:
        {
          return /*#__PURE__*/_jsxs(UIErrorMessage, {
            children: [/*#__PURE__*/_jsx("p", {
              children: /*#__PURE__*/_jsx(FormattedMessage, {
                message: this.langKey('description')
              })
            }), /*#__PURE__*/_jsx("p", {
              children: /*#__PURE__*/_jsx(FormattedMessage, {
                message: this.langKeyForCanContactSupport('nextStep')
              })
            })]
          });
        }
    }
  }
  maybeRenderNavMarker() {
    if (this.isErrorPageExpected) {
      return /*#__PURE__*/_jsx(NavMarker, {
        name: "ERROR_PAGE_RENDERED_EXPECTEDLY"
      });
    }
    return /*#__PURE__*/_jsx(NavMarker, {
      name: "ERROR_PAGE_RENDERED"
    });
  }
  render() {
    return /*#__PURE__*/_jsxs(I18nReadyContainer, {
      children: [/*#__PURE__*/_jsx(UIFlex, {
        align: "center",
        justify: "center",
        direction: "column",
        wrap: "wrap",
        children: this.state.isCategoryLoading && /*#__PURE__*/_jsx(UILoadingSpinner, {
          minHeight: '100vh'
        }) || this.renderError()
      }), this.maybeRenderNavMarker()]
    });
  }
}
ErrorPage.propTypes = {
  appErrorCategory: PropTypes.string,
  responseErrorType: PropTypes.string,
  canContactSupport: PropTypes.bool.isRequired,
  params: PropTypes.object.isRequired,
  shouldNotRenderDashboardLink: PropTypes.bool,
  securityState: PropTypes.string
};
export default withPathParams(ErrorPage);